import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { GET_DTC_ALERTS } from "./realQueries";
import { DTC } from "./types";

// Standard OBD-II DTC code-prefix convention — a lossless, well-known mapping,
// not an invented classification (unlike "severity", which real data has no equivalent for).
const SYSTEM_BY_PREFIX: Record<string, string> = {
  P: "Powertrain",
  B: "Body",
  C: "Chassis",
  U: "Network",
};

function systemForCode(code: string): string {
  return SYSTEM_BY_PREFIX[code?.[0]?.toUpperCase()] ?? "Unknown";
}

interface RawDtcEvent {
  deviceEsn: string;
  eventTime?: string | null;
  description?: string | null;
  codeType?: string | null;
  address?: { city?: string | null; street?: string | null } | null;
}

const LOOKBACK_HOURS = 24;

export function useVehicleDtc(deviceEsns: string[]) {
  const startDate = useMemo(() => DateTime.now().minus({ hours: LOOKBACK_HOURS }).toUTC().toISO(), []);
  const endDate = useMemo(() => DateTime.now().toUTC().toISO(), []);

  const { data, loading, error, refetch } = useQuery<{ telematicDtcEvents: RawDtcEvent[] }>(GET_DTC_ALERTS, {
    variables: { deviceEsn: deviceEsns, startDate, endDate },
    skip: deviceEsns.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const dtcByEsn = useMemo(() => {
    const grouped: Record<string, DTC[]> = {};
    (data?.telematicDtcEvents ?? []).forEach((event) => {
      const code = event.codeType ?? "Unknown";
      const entry: DTC = {
        code,
        description: event.description ?? "No description available",
        system: systemForCode(code),
        detectedAt: event.eventTime ? DateTime.fromISO(event.eventTime).toFormat("dd LLL yyyy, HH:mm") : "Unknown time",
        location: [event.address?.street, event.address?.city].filter(Boolean).join(", ") || undefined,
      };
      if (!grouped[event.deviceEsn]) grouped[event.deviceEsn] = [];
      grouped[event.deviceEsn].push(entry);
    });
    return grouped;
  }, [data]);

  return { dtcByEsn, loading, error, refetch };
}
